import { match } from 'ts-pattern';
import { $Post } from '../__generated/enhanced-model.js';
import { HandrailType, MetalPostStyleType, PositionType, PostStainlessFinishType, PostType, ThinIntermediatePostType } from '../__generated/model/index.js';
import { PartsBindingBuilder } from '../sdk/data-binding.js';
import { Materials } from './materials.js';
import { ObjectNames, getObjectInfo } from './objects.js';
const parts = PartsBindingBuilder.from('parts', $Post, [
    '$postType',
    '$postStyle',
    '$postFinish',
    '$thinIntermediatePostType',
    '$handrailType',
    'positionType'
], (data, s)=>{
    // A bit of a hack to scale the thin intermediate posts to fit the handrail height without tweaking the model itself
    const scaleY = data.postObjectName === ObjectNames.postThinIntermediate ? 0.995 : 1;
    const defaultPostBody = {
        x: s.x,
        y: s.y,
        z: s.z,
        scaleY,
        key: `${s.key}_post`,
        morphTargetInfluences: {
            length: {
                value: 990,
                scaleZero: 956,
                scaleOne: 1056
            },
            angle: {
                value: data.sloped ? s.slope : 0,
                scaleZero: 0,
                scaleOne: Math.PI / 4
            }
        }
    };
    const defaultTopBody = {
        x: s.x,
        y: s.y + 950,
        z: s.z,
        key: `${s.key}_top`
    };
    const postObjectInfo = getObjectInfo(data.postObjectName);
    const parts = [
        {
            skus: data.skus.map((sku)=>({
                    sku,
                    metadata: {}
                })),
            objects: [
                {
                    fileUrl: postObjectInfo.fileUrl,
                    objectName: data.postObjectName,
                    material: data.materialName,
                    metalness: s.$postStyle === MetalPostStyleType.Custom ? 0.6 : undefined,
                    ...defaultPostBody
                }
            ]
        }
    ];
    // Add top part if it exists
    if (data.topObjectName) {
        const rotationY = data.rotationY ?? 0;
        const slopeDirection = data.reverseSlope ? 1 : -1;
        const topObjectInfo = getObjectInfo(data.topObjectName);
        parts.push({
            skus: [],
            objects: [
                {
                    fileUrl: topObjectInfo.fileUrl,
                    objectName: data.topObjectName,
                    material: data.materialName,
                    rotationY,
                    rotationZ: data.sloped ? s.slope * slopeDirection : 0,
                    ...defaultTopBody
                }
            ]
        });
    }
    if (data.grommetSku != null) {
        const minHeight = 0;
        const maxHeight = 950;
        const wireHeightStep = (maxHeight - minHeight) / (s.$deckItem.$deck.$numberOfWires + 1);
        const postSize = 50;
        const thinPostSize = 10;
        const grommetOffset = match(s.positionType).with(PositionType.ThinIntermediate, ()=>thinPostSize / 2).otherwise(()=>postSize / 2);
        const leftGrommetX = match(s.positionType).with(PositionType.AngledIntermediate, ()=>s.x).otherwise(()=>s.x - grommetOffset);
        const leftGrommetZ = match(s.positionType).with(PositionType.AngledIntermediate, ()=>s.z - grommetOffset).otherwise(()=>s.z);
        const rightGrommetX = match(s.positionType).with(PositionType.AngledIntermediate, ()=>s.x).otherwise(()=>s.x + grommetOffset);
        const rightGrommetZ = match(s.positionType).with(PositionType.AngledIntermediate, ()=>s.z + grommetOffset).otherwise(()=>s.z);
        const leftRotationY = match(s.positionType).with(PositionType.AngledIntermediate, ()=>Math.PI / 2).otherwise(()=>Math.PI);
        const rightRotationY = match(s.positionType).with(PositionType.AngledIntermediate, ()=>-Math.PI / 2).otherwise(()=>0);
        const grommetObjectInfo = getObjectInfo(ObjectNames.grommetLarge);
        const grommetParts = Array.from({
            length: s.$deckItem.$deck.$numberOfWires
        }, (_, i)=>{
            const leftY = s.y + maxHeight - (i + 1) * wireHeightStep + grommetOffset * Math.tan(s.slope);
            const rightY = s.y + maxHeight - (i + 1) * wireHeightStep - grommetOffset * Math.tan(s.slope) - 3;
            return {
                skus: [
                    {
                        sku: data.grommetSku,
                        metadata: {}
                    },
                    {
                        sku: data.grommetSku,
                        metadata: {}
                    }
                ],
                objects: [
                    {
                        objectName: ObjectNames.grommetLarge,
                        fileUrl: grommetObjectInfo.fileUrl,
                        key: `${s.key}_grommet_${i}_left`,
                        x: leftGrommetX,
                        y: leftY,
                        z: leftGrommetZ,
                        material: data.materialName,
                        rotationY: leftRotationY
                    },
                    {
                        objectName: ObjectNames.grommetLarge,
                        fileUrl: grommetObjectInfo.fileUrl,
                        key: `${s.key}_grommet_${i}_right`,
                        x: rightGrommetX,
                        y: rightY,
                        z: rightGrommetZ,
                        material: data.materialName,
                        rotationY: rightRotationY
                    }
                ]
            };
        });
        parts.push(...grommetParts);
    }
    return parts;
}, [
    // #region All wooden posts
    {
        keys: {
            $postType: PostType.Timber,
            $postStyle: MetalPostStyleType.values(),
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            // Lower values override the top ones, so this does not need to filter out the ThinIntermediate
            positionType: PositionType.values()
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postWooden,
            topObjectName: null,
            materialName: Materials.Wood,
            scaleWood: true
        }
    },
    {
        keys: {
            $postType: PostType.Timber,
            $postStyle: MetalPostStyleType.values(),
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: [
                PositionType.AngledIntermediate,
                PositionType.StairsTopLeftEnd,
                PositionType.StairsBottomLeftEnd,
                PositionType.StairsTopRightEnd,
                PositionType.StairsBottomRightEnd
            ]
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postWooden,
            topObjectName: null,
            materialName: Materials.Wood,
            scaleWood: true,
            sloped: true
        }
    },
    // #endregion
    // #region Thin intermediate posts with specific materials
    {
        keys: {
            $postType: PostType.Timber,
            $postStyle: MetalPostStyleType.values(),
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.BlackAlu,
            $handrailType: HandrailType.values(),
            positionType: PositionType.ThinIntermediate
        },
        values: {
            skus: [
                'INT5010A-B'
            ],
            postObjectName: ObjectNames.postThinIntermediate,
            topObjectName: null,
            materialName: Materials.BlackAlu,
            grommetSku: ObjectNames.grommetSmallBlack
        }
    },
    {
        keys: {
            $postType: PostType.Timber,
            $postStyle: MetalPostStyleType.values(),
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.MirrorStainless,
            $handrailType: HandrailType.values(),
            positionType: PositionType.ThinIntermediate
        },
        values: {
            skus: [
                'INT5010-MP'
            ],
            postObjectName: ObjectNames.postThinIntermediate,
            topObjectName: null,
            materialName: Materials.MetalMirrorPolish,
            grommetSku: ObjectNames.grommetSmallSatin
        }
    },
    {
        keys: {
            $postType: PostType.Timber,
            $postStyle: MetalPostStyleType.values(),
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.SatinStainless,
            $handrailType: HandrailType.values(),
            positionType: PositionType.ThinIntermediate
        },
        values: {
            skus: [
                'INT5010-SF'
            ],
            postObjectName: ObjectNames.postThinIntermediate,
            topObjectName: null,
            materialName: Materials.MetalSatin,
            grommetSku: ObjectNames.grommetSmallSatin
        }
    },
    // #endregion
    // #region Square aluminium posts
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.LeftEnd
        },
        values: {
            skus: [
                'ALP-END50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopIntermediate,
            materialName: Materials.BlackAlu,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.RightEnd
        },
        values: {
            skus: [
                'ALP-END50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopIntermediate,
            materialName: Materials.BlackAlu
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatFlatObtuse
        },
        values: {
            skus: [
                'ALP-END50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEnd,
            materialName: Materials.BlackAlu,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatFlatAcuteLeft
        },
        values: {
            skus: [
                'ALP-END50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEnd,
            materialName: Materials.BlackAlu,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatFlatAcuteRight
        },
        values: {
            skus: [
                'ALP-END50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEnd,
            materialName: Materials.BlackAlu
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.Intermediate
        },
        values: {
            skus: [
                'ALP-INT50960-BL'
            ],
            grommetSku: ObjectNames.grommetLargeBlack,
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopIntermediate,
            materialName: Materials.BlackAlu
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.ThinIntermediate
        },
        values: {
            skus: [
                'INT5010A-B'
            ],
            grommetSku: ObjectNames.grommetSmallBlack,
            postObjectName: ObjectNames.postThinIntermediate,
            topObjectName: null,
            materialName: Materials.BlackAlu
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.MidPost
        },
        values: {
            skus: [
                'ALP-MID50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopIntermediate,
            materialName: Materials.BlackAlu
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatFlatRight
        },
        values: {
            skus: [
                'ALP-CNR50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopCorner,
            materialName: Materials.BlackAlu,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.AngledIntermediate
        },
        values: {
            skus: [
                'ALP-INT50ANG-BL'
            ],
            grommetSku: ObjectNames.grommetLargeAngledBlack,
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopIntermediate,
            materialName: Materials.BlackAlu,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsTopLeftEnd
        },
        values: {
            skus: [
                'ALP-END50ANG-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEndAngled,
            materialName: Materials.BlackAlu,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsBottomLeftEnd
        },
        values: {
            skus: [
                'ALP-END50ANG-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEndAngled,
            materialName: Materials.BlackAlu,
            rotationY: Math.PI / 2,
            sloped: true,
            reverseSlope: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsTopRightEnd
        },
        values: {
            skus: [
                'ALP-END50ANG-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEndAngled,
            materialName: Materials.BlackAlu,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsBottomRightEnd
        },
        values: {
            skus: [
                'ALP-END50ANG-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEndAngled,
            materialName: Materials.BlackAlu,
            rotationY: Math.PI / 2,
            sloped: true,
            reverseSlope: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsStraightTopLeft
        },
        values: {
            skus: [
                'ALP-STPMID50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEnd,
            materialName: Materials.BlackAlu,
            rotationY: -Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsStraightBottomLeft
        },
        values: {
            skus: [
                'ALP-STPMID50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEnd,
            materialName: Materials.BlackAlu,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsStraightTopRight
        },
        values: {
            skus: [
                'ALP-STPMID50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEnd,
            materialName: Materials.BlackAlu,
            rotationY: -Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsStraightBottomRight
        },
        values: {
            skus: [
                'ALP-STPMID50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEnd,
            materialName: Materials.BlackAlu,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsCornerTopLeft
        },
        values: {
            skus: [
                'ALP-STPL50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEnd,
            materialName: Materials.BlackAlu
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsCornerBottomLeft
        },
        values: {
            skus: [
                'ALP-STPR50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEnd,
            materialName: Materials.BlackAlu,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsCornerTopRight
        },
        values: {
            skus: [
                'ALP-STPR50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEnd,
            materialName: Materials.BlackAlu,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsCornerBottomRight
        },
        values: {
            skus: [
                'ALP-STPL50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEnd,
            materialName: Materials.BlackAlu,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsFlatOtherTopLeft
        },
        values: {
            skus: [
                'ALP-END50ANG-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEndAngled,
            materialName: Materials.BlackAlu,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsFlatOtherBottomLeft
        },
        values: {
            skus: [
                'ALP-END50ANG-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEndAngled,
            materialName: Materials.BlackAlu,
            rotationY: Math.PI / 2,
            sloped: true,
            reverseSlope: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsFlatOtherTopRight
        },
        values: {
            skus: [
                'ALP-END50ANG-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEndAngled,
            materialName: Materials.BlackAlu,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsFlatOtherBottomRight
        },
        values: {
            skus: [
                'ALP-END50ANG-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEndAngled,
            materialName: Materials.BlackAlu,
            rotationY: Math.PI / 2,
            sloped: true,
            reverseSlope: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsOtherTopLeft
        },
        values: {
            skus: [
                'ALP-END50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEnd,
            materialName: Materials.BlackAlu
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsOtherTopRight
        },
        values: {
            skus: [
                'ALP-END50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEnd,
            materialName: Materials.BlackAlu,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsOtherBottomLeft
        },
        values: {
            skus: [
                'ALP-END50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEnd,
            materialName: Materials.BlackAlu,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareAlu,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsOtherBottomRight
        },
        values: {
            skus: [
                'ALP-END50960-BL'
            ],
            postObjectName: ObjectNames.postAluminium,
            topObjectName: ObjectNames.aluminiumTopEnd,
            materialName: Materials.BlackAlu
        }
    },
    // #endregion
    // #region Custom posts
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.LeftEnd
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.RightEnd
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatFlatObtuse
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatFlatAcuteLeft
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatFlatAcuteRight
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.Intermediate
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.BlackAlu,
            $handrailType: HandrailType.values(),
            positionType: PositionType.ThinIntermediate
        },
        values: {
            skus: [
                'INT5010A-B'
            ],
            postObjectName: ObjectNames.postThinIntermediate,
            topObjectName: null,
            materialName: Materials.BlackAlu,
            grommetSku: ObjectNames.grommetSmallBlack
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.MirrorStainless,
            $handrailType: HandrailType.values(),
            positionType: PositionType.ThinIntermediate
        },
        values: {
            skus: [
                'INT5010-MP'
            ],
            postObjectName: ObjectNames.postThinIntermediate,
            topObjectName: null,
            materialName: Materials.MetalMirrorPolish,
            grommetSku: ObjectNames.grommetSmallSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.SatinStainless,
            $handrailType: HandrailType.values(),
            positionType: PositionType.ThinIntermediate
        },
        values: {
            skus: [
                'INT5010-SF'
            ],
            postObjectName: ObjectNames.postThinIntermediate,
            topObjectName: null,
            materialName: Materials.MetalSatin,
            grommetSku: ObjectNames.grommetSmallSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.MidPost
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatFlatRight
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat3,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.AngledIntermediate
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            sloped: true,
            rotationY: -Math.PI / 2,
            grommetSku: ObjectNames.grommetStairAngledBlack
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsTopLeftEnd
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsTopRightEnd
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsBottomLeftEnd
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2,
            sloped: true,
            reverseSlope: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsBottomRightEnd
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2,
            sloped: true,
            reverseSlope: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsStraightTopLeft
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsStraightBottomLeft
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsStraightTopRight
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsStraightBottomRight
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsCornerTopLeft
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat6,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsCornerBottomLeft
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat5,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsCornerTopRight
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat5,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsCornerBottomRight
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat6,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsFlatOtherTopLeft
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsFlatOtherBottomLeft
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsFlatOtherBottomRight
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.StairsFlatOtherTopRight
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsOtherTopLeft
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsOtherBottomLeft
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsOtherTopRight
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.Custom,
            $postFinish: PostStainlessFinishType.values(),
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.values(),
            positionType: PositionType.FlatStairsOtherBottomRight
        },
        values: {
            skus: [],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin
        }
    },
    // #endregion
    // #region Round stainless
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.LeftEnd
        },
        values: {
            skus: [
                'SSRP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.LeftEnd
        },
        values: {
            skus: [
                'SSRP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.LeftEnd
        },
        values: {
            skus: [
                'SSRP-END50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.LeftEnd
        },
        values: {
            skus: [
                'SSRP-END50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.RightEnd
        },
        values: {
            skus: [
                'SSRP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.RightEnd
        },
        values: {
            skus: [
                'SSRP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.RightEnd
        },
        values: {
            skus: [
                'SSRP-END50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.RightEnd
        },
        values: {
            skus: [
                'SSRP-END50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatObtuse
        },
        values: {
            skus: [
                'SSRP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatObtuse
        },
        values: {
            skus: [
                'SSRP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatObtuse
        },
        values: {
            skus: [
                'SSRP-END50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatObtuse
        },
        values: {
            skus: [
                'SSRP-END50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatAcuteLeft
        },
        values: {
            skus: [
                'SSRP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatAcuteLeft
        },
        values: {
            skus: [
                'SSRP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatAcuteLeft
        },
        values: {
            skus: [
                'SSRP-END50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatAcuteLeft
        },
        values: {
            skus: [
                'SSRP-END50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound4,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatAcuteRight
        },
        values: {
            skus: [
                'SSRP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatAcuteRight
        },
        values: {
            skus: [
                'SSRP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatAcuteRight
        },
        values: {
            skus: [
                'SSRP-END50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound4,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatAcuteRight
        },
        values: {
            skus: [
                'SSRP-END50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound4,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.Intermediate
        },
        values: {
            skus: [
                'SSRP-INT50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalSatin,
            grommetSku: ObjectNames.grommetSmallSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.Intermediate
        },
        values: {
            skus: [
                'SSRP-INT50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalMirrorPolish,
            grommetSku: ObjectNames.grommetSmallSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.Intermediate
        },
        values: {
            skus: [
                'SSRP-INT50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalSatin,
            grommetSku: ObjectNames.grommetSmallSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.Intermediate
        },
        values: {
            skus: [
                'SSRP-INT50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalMirrorPolish,
            grommetSku: ObjectNames.grommetSmallSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.MidPost
        },
        values: {
            skus: [
                'SSRP-MID50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.MidPost
        },
        values: {
            skus: [
                'SSRP-MID50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.MidPost
        },
        values: {
            skus: [
                'SSRP-MID50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.MidPost
        },
        values: {
            skus: [
                'SSRP-MID50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatRight
        },
        values: {
            skus: [
                'SSRP-CNR50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat3,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatRight
        },
        values: {
            skus: [
                'SSRP-CNR50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat3,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatRight
        },
        values: {
            skus: [
                'SSRP-CNR50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound3,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatRight
        },
        values: {
            skus: [
                'SSRP-CNR50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound3,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.AngledIntermediate
        },
        values: {
            skus: [
                'SSRP-STPI50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            grommetSku: ObjectNames.grommetStairAngledSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.AngledIntermediate
        },
        values: {
            skus: [
                'SSRP-STPI50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            grommetSku: ObjectNames.grommetStairAngledSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.AngledIntermediate
        },
        values: {
            skus: [
                'SSRP-STPI50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalSatin,
            grommetSku: ObjectNames.grommetStairAngledSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.AngledIntermediate
        },
        values: {
            skus: [
                'SSRP-STPI50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalMirrorPolish,
            grommetSku: ObjectNames.grommetStairAngledSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsTopLeftEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsTopLeftEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsTopLeftEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsTopLeftEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsBottomLeftEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsBottomLeftEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsBottomLeftEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsBottomLeftEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsTopRightEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsTopRightEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsTopRightEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsTopRightEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsBottomRightEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsBottomRightEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsBottomRightEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsBottomRightEnd
        },
        values: {
            skus: [
                'SSRP-STEND50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightTopLeft
        },
        values: {
            skus: [
                'SSRP-STMID50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightTopLeft
        },
        values: {
            skus: [
                'SSRP-STMID50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightTopLeft
        },
        values: {
            skus: [
                'SSRP-STMID50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightTopLeft
        },
        values: {
            skus: [
                'SSRP-STMID50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightBottomLeft
        },
        values: {
            skus: [
                'SSRP-STMID50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightBottomLeft
        },
        values: {
            skus: [
                'SSRP-STMID50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightBottomLeft
        },
        values: {
            skus: [
                'SSRP-STMID50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightBottomLeft
        },
        values: {
            skus: [
                'SSRP-STMID50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightTopRight
        },
        values: {
            skus: [
                'SSRP-STMID50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightTopRight
        },
        values: {
            skus: [
                'SSRP-STMID50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightTopRight
        },
        values: {
            skus: [
                'SSRP-STMID50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightTopRight
        },
        values: {
            skus: [
                'SSRP-STMID50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightBottomRight
        },
        values: {
            skus: [
                'SSRP-STMID50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightBottomRight
        },
        values: {
            skus: [
                'SSRP-STMID50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightBottomRight
        },
        values: {
            skus: [
                'SSRP-STMID50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightBottomRight
        },
        values: {
            skus: [
                'SSRP-STMID50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerTopLeft
        },
        values: {
            skus: [
                'SSRP-STPL50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat6,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerTopLeft
        },
        values: {
            skus: [
                'SSRP-STPL50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat6,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerTopLeft
        },
        values: {
            skus: [
                'SSRP-STPL50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound6,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerTopLeft
        },
        values: {
            skus: [
                'SSRP-STPL50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound6,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerBottomLeft
        },
        values: {
            skus: [
                'SSRP-STPR50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat5,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerBottomLeft
        },
        values: {
            skus: [
                'SSRP-STPR50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat5,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerBottomLeft
        },
        values: {
            skus: [
                'SSRP-STPR50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound5,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerBottomLeft
        },
        values: {
            skus: [
                'SSRP-STPR50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound5,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerTopRight
        },
        values: {
            skus: [
                'SSRP-STPR50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat5,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerTopRight
        },
        values: {
            skus: [
                'SSRP-STPR50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat5,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerTopRight
        },
        values: {
            skus: [
                'SSRP-STPR50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound5,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerTopRight
        },
        values: {
            skus: [
                'SSRP-STPR50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound5,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerBottomRight
        },
        values: {
            skus: [
                'SSRP-STPL50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat6,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerBottomRight
        },
        values: {
            skus: [
                'SSRP-STPL50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat6,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerBottomRight
        },
        values: {
            skus: [
                'SSRP-STPL50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound6,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerBottomRight
        },
        values: {
            skus: [
                'SSRP-STPL50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopRound6,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherTopLeft
        },
        values: {
            skus: [
                'SSRP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherTopLeft
        },
        values: {
            skus: [
                'SSRP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherTopLeft
        },
        values: {
            skus: [
                'SSRP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherTopLeft
        },
        values: {
            skus: [
                'SSRP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherBottomLeft
        },
        values: {
            skus: [
                'SSRP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherBottomLeft
        },
        values: {
            skus: [
                'SSRP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherBottomLeft
        },
        values: {
            skus: [
                'SSRP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherBottomLeft
        },
        values: {
            skus: [
                'SSRP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherTopRight
        },
        values: {
            skus: [
                'SSRP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherTopRight
        },
        values: {
            skus: [
                'SSRP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherTopRight
        },
        values: {
            skus: [
                'SSRP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherTopRight
        },
        values: {
            skus: [
                'SSRP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherBottomRight
        },
        values: {
            skus: [
                'SSRP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherBottomRight
        },
        values: {
            skus: [
                'SSRP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherBottomRight
        },
        values: {
            skus: [
                'SSRP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherBottomRight
        },
        values: {
            skus: [
                'SSRP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherTopLeft
        },
        values: {
            skus: [
                'SSRP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherTopLeft
        },
        values: {
            skus: [
                'SSRP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherTopLeft
        },
        values: {
            skus: [
                'SSRP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherTopLeft
        },
        values: {
            skus: [
                'SSRP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherBottomLeft
        },
        values: {
            skus: [
                'SSRP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherBottomLeft
        },
        values: {
            skus: [
                'SSRP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherBottomLeft
        },
        values: {
            skus: [
                'SSRP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherBottomLeft
        },
        values: {
            skus: [
                'SSRP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherTopRight
        },
        values: {
            skus: [
                'SSRP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherTopRight
        },
        values: {
            skus: [
                'SSRP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherTopRight
        },
        values: {
            skus: [
                'SSRP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherTopRight
        },
        values: {
            skus: [
                'SSRP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherBottomRight
        },
        values: {
            skus: [
                'SSRP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherBottomRight
        },
        values: {
            skus: [
                'SSRP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherBottomRight
        },
        values: {
            skus: [
                'SSRP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherBottomRight
        },
        values: {
            skus: [
                'SSRP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRound,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    // #endregion
    // #region Square stainless
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.LeftEnd
        },
        values: {
            skus: [
                'SSSP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.LeftEnd
        },
        values: {
            skus: [
                'SSSP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.LeftEnd
        },
        values: {
            skus: [
                'SSSP-END50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.LeftEnd
        },
        values: {
            skus: [
                'SSSP-END50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.RightEnd
        },
        values: {
            skus: [
                'SSSP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.RightEnd
        },
        values: {
            skus: [
                'SSSP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.RightEnd
        },
        values: {
            skus: [
                'SSSP-END50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.RightEnd
        },
        values: {
            skus: [
                'SSSP-END50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatObtuse
        },
        values: {
            skus: [
                'SSSP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatObtuse
        },
        values: {
            skus: [
                'SSSP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatObtuse
        },
        values: {
            skus: [
                'SSSP-END50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatObtuse
        },
        values: {
            skus: [
                'SSSP-END50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatAcuteLeft
        },
        values: {
            skus: [
                'SSSP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatAcuteLeft
        },
        values: {
            skus: [
                'SSSP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatAcuteLeft
        },
        values: {
            skus: [
                'SSSP-END50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatAcuteLeft
        },
        values: {
            skus: [
                'SSSP-END50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound4,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatAcuteRight
        },
        values: {
            skus: [
                'SSSP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatAcuteRight
        },
        values: {
            skus: [
                'SSSP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatAcuteRight
        },
        values: {
            skus: [
                'SSSP-END50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound4,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatAcuteRight
        },
        values: {
            skus: [
                'SSSP-END50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound4,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.Intermediate
        },
        values: {
            skus: [
                'SSSP-INT50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalSatin,
            grommetSku: ObjectNames.grommetSmallSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.Intermediate
        },
        values: {
            skus: [
                'SSSP-INT50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalMirrorPolish,
            grommetSku: ObjectNames.grommetSmallSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.Intermediate
        },
        values: {
            skus: [
                'SSSP-INT50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalSatin,
            grommetSku: ObjectNames.grommetSmallSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.Intermediate
        },
        values: {
            skus: [
                'SSSP-INT50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalMirrorPolish,
            grommetSku: ObjectNames.grommetSmallSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.ThinIntermediate
        },
        values: {
            skus: [
                'INT5010A-B'
            ],
            postObjectName: ObjectNames.postThinIntermediate,
            topObjectName: null,
            materialName: Materials.MetalSatin,
            grommetSku: ObjectNames.grommetSmallBlack
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.ThinIntermediate
        },
        values: {
            skus: [
                'INT5010-MP'
            ],
            postObjectName: ObjectNames.postThinIntermediate,
            topObjectName: null,
            materialName: Materials.MetalMirrorPolish,
            grommetSku: ObjectNames.grommetSmallSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.ThinIntermediate
        },
        values: {
            skus: [
                'SSSP-INT50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalSatin,
            grommetSku: ObjectNames.grommetSmallSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.ThinIntermediate
        },
        values: {
            skus: [
                'SSSP-INT50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalMirrorPolish,
            grommetSku: ObjectNames.grommetSmallSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.MidPost
        },
        values: {
            skus: [
                'SSSP-MID50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.MidPost
        },
        values: {
            skus: [
                'SSSP-MID50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat1,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.MidPost
        },
        values: {
            skus: [
                'SSSP-MID50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.MidPost
        },
        values: {
            skus: [
                'SSSP-MID50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound1,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatRight
        },
        values: {
            skus: [
                'SSSP-CNR50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat3,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatFlatRight
        },
        values: {
            skus: [
                'SSSP-CNR50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat3,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatRight
        },
        values: {
            skus: [
                'SSSP-CNR50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound3,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatFlatRight
        },
        values: {
            skus: [
                'SSSP-CNR50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound3,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.AngledIntermediate
        },
        values: {
            skus: [
                'SSSP-STPI50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            grommetSku: ObjectNames.grommetStairAngledSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.AngledIntermediate
        },
        values: {
            skus: [
                'SSSP-STPI50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            grommetSku: ObjectNames.grommetStairAngledSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.AngledIntermediate
        },
        values: {
            skus: [
                'SSSP-STPI50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalSatin,
            grommetSku: ObjectNames.grommetStairAngledSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.AngledIntermediate
        },
        values: {
            skus: [
                'SSSP-STPI50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalMirrorPolish,
            grommetSku: ObjectNames.grommetStairAngledSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsTopLeftEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsTopLeftEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsTopLeftEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsTopLeftEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsBottomLeftEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsBottomLeftEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsBottomLeftEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsBottomLeftEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsTopRightEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsTopRightEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsTopRightEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsTopRightEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsBottomRightEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsBottomRightEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsBottomRightEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsBottomRightEnd
        },
        values: {
            skus: [
                'SSSP-STEND50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightTopLeft
        },
        values: {
            skus: [
                'SSSP-STMID50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightTopLeft
        },
        values: {
            skus: [
                'SSSP-STMID50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightTopLeft
        },
        values: {
            skus: [
                'SSSP-STMID50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightTopLeft
        },
        values: {
            skus: [
                'SSSP-STMID50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightBottomLeft
        },
        values: {
            skus: [
                'SSSP-STMID50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightBottomLeft
        },
        values: {
            skus: [
                'SSSP-STMID50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightBottomLeft
        },
        values: {
            skus: [
                'SSSP-STMID50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightBottomLeft
        },
        values: {
            skus: [
                'SSSP-STMID50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightTopRight
        },
        values: {
            skus: [
                'SSSP-STMID50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightTopRight
        },
        values: {
            skus: [
                'SSSP-STMID50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightTopRight
        },
        values: {
            skus: [
                'SSSP-STMID50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightTopRight
        },
        values: {
            skus: [
                'SSSP-STMID50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightBottomRight
        },
        values: {
            skus: [
                'SSSP-STMID50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsStraightBottomRight
        },
        values: {
            skus: [
                'SSSP-STMID50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightBottomRight
        },
        values: {
            skus: [
                'SSSP-STMID50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsStraightBottomRight
        },
        values: {
            skus: [
                'SSSP-STMID50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound7,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerTopLeft
        },
        values: {
            skus: [
                'SSSP-STPL50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat6,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerTopLeft
        },
        values: {
            skus: [
                'SSSP-STPL50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat6,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerTopLeft
        },
        values: {
            skus: [
                'SSSP-STPL50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound6,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerTopLeft
        },
        values: {
            skus: [
                'SSSP-STPL50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound6,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerBottomLeft
        },
        values: {
            skus: [
                'SSSP-STPR50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat5,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerBottomLeft
        },
        values: {
            skus: [
                'SSSP-STPR50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat5,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerBottomLeft
        },
        values: {
            skus: [
                'SSSP-STPR50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound5,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerBottomLeft
        },
        values: {
            skus: [
                'SSSP-STPR50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound5,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerTopRight
        },
        values: {
            skus: [
                'SSSP-STPR50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat5,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerTopRight
        },
        values: {
            skus: [
                'SSSP-STPR50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat5,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerTopRight
        },
        values: {
            skus: [
                'SSSP-STPR50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound5,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerTopRight
        },
        values: {
            skus: [
                'SSSP-STPR50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound5,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerBottomRight
        },
        values: {
            skus: [
                'SSSP-STPL50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat6,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsCornerBottomRight
        },
        values: {
            skus: [
                'SSSP-STPL50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat6,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerBottomRight
        },
        values: {
            skus: [
                'SSSP-STPL50960R-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound6,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsCornerBottomRight
        },
        values: {
            skus: [
                'SSSP-STPL50960R-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopRound6,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherTopLeft
        },
        values: {
            skus: [
                'SSSP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherTopLeft
        },
        values: {
            skus: [
                'SSSP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherTopLeft
        },
        values: {
            skus: [
                'SSSP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherTopLeft
        },
        values: {
            skus: [
                'SSSP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherBottomLeft
        },
        values: {
            skus: [
                'SSSP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherBottomLeft
        },
        values: {
            skus: [
                'SSSP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherBottomLeft
        },
        values: {
            skus: [
                'SSSP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherBottomLeft
        },
        values: {
            skus: [
                'SSSP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherTopRight
        },
        values: {
            skus: [
                'SSSP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherTopRight
        },
        values: {
            skus: [
                'SSSP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherTopRight
        },
        values: {
            skus: [
                'SSSP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherTopRight
        },
        values: {
            skus: [
                'SSSP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherBottomRight
        },
        values: {
            skus: [
                'SSSP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.FlatStairsOtherBottomRight
        },
        values: {
            skus: [
                'SSSP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherBottomRight
        },
        values: {
            skus: [
                'SSSP-END50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.FlatStairsOtherBottomRight
        },
        values: {
            skus: [
                'SSSP-END50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat4,
            materialName: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherTopLeft
        },
        values: {
            skus: [
                'SSSP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherTopLeft
        },
        values: {
            skus: [
                'SSSP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherTopLeft
        },
        values: {
            skus: [
                'SSSP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherTopLeft
        },
        values: {
            skus: [
                'SSSP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherBottomLeft
        },
        values: {
            skus: [
                'SSSP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherBottomLeft
        },
        values: {
            skus: [
                'SSSP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherBottomLeft
        },
        values: {
            skus: [
                'SSSP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherBottomLeft
        },
        values: {
            skus: [
                'SSSP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherTopRight
        },
        values: {
            skus: [
                'SSSP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherTopRight
        },
        values: {
            skus: [
                'SSSP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherTopRight
        },
        values: {
            skus: [
                'SSSP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherTopRight
        },
        values: {
            skus: [
                'SSSP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherBottomRight
        },
        values: {
            skus: [
                'SSSP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: [
                HandrailType.FlatTimber,
                HandrailType.SquareStainless
            ],
            positionType: PositionType.StairsFlatOtherBottomRight
        },
        values: {
            skus: [
                'SSSP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherBottomRight
        },
        values: {
            skus: [
                'SSSP-STEND50960F-SF'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $postStyle: MetalPostStyleType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            $thinIntermediatePostType: ThinIntermediatePostType.values(),
            $handrailType: HandrailType.RoundStainless,
            positionType: PositionType.StairsFlatOtherBottomRight
        },
        values: {
            skus: [
                'SSSP-STEND50960F-MP'
            ],
            postObjectName: ObjectNames.postStainlessSteelAngledRoundPlain,
            topObjectName: ObjectNames.stainlessSteelTopFlat2,
            materialName: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    }
]);
export const PostDataBindings = {
    parts
};

//# sourceMappingURL=post.js.map